import { useState, useCallback } from 'react';
import { usePost } from '../API';
import { useAsyncEffect } from '../Hooks/AsyncEffect';

export const isAidKitDocumentsUrl = (content: unknown): content is string => {
  return typeof content === 'string' && content.includes('aidkit-documents');
};

const removeQueryParams = (url: string) => {
  return url.split('?')[0];
};

const getSearchParamsFromUrl = (url: string) => {
  try {
    const { searchParams } = new URL(url);
    return searchParams;
  } catch {
    return new URLSearchParams();
  }
};

const signedUrlIsExpired = (url: string) => {
  const searchParams = getSearchParamsFromUrl(url);
  const amzDateString = searchParams.get('X-Amz-Date');
  const expiryTimeString = searchParams.get('X-Amz-Expires');

  if (!amzDateString || !expiryTimeString) {
    return false;
  }

  // Amz Dates are YYYYMMDDTHHMMSSZ and need to be converted to YYYY-MM-DDTHH:MM:SSZ
  const formattedAmzDateString = amzDateString.replace(
    /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/,
    '$1-$2-$3T$4:$5:$6Z',
  );
  const amzDate = new Date(formattedAmzDateString);
  const expiryTime = parseInt(expiryTimeString, 10) * 1000;

  if (isNaN(+amzDate) || isNaN(expiryTime)) {
    return false;
  }

  return Date.now() > +amzDate + expiryTime;
};

/**
 * Given a list of pre-signed s3 urls, returns a mapping from the original url to a signed url that refreshes if it's expired.
 */
export const useSignedS3Urls = (s3Urls: string[]) => {
  const getSignedUrls = usePost('/v2/document/view');

  const [currentSignedS3Urls, setCurrentSignedS3Urls] = useState<Record<string, string | undefined>>({});

  const refreshExpiredUrls = useCallback(async (expiredUrls: string[]) => {
    if (expiredUrls.length === 0) {
      return {};
    }

    // The AWS query params make this way too large to feasibly send and aren't needed, so strip them
    // but keep a record of the original url so the calling code doesn't need to worry about this.
    const urlWithoutQueryParamsToOriginalUrl = Object.fromEntries((expiredUrls.map((s3Url) => [removeQueryParams(s3Url), s3Url])));

    const newlySignedS3Urls = await getSignedUrls({
      paths: expiredUrls.map(removeQueryParams),
    });

    if (!newlySignedS3Urls?.paths) {
      return {};
    }

    const newSignedS3Urls: Record<string, string> = {};
    Object.entries(newlySignedS3Urls.paths).forEach(([key, value]) => {
      const originalUrl = urlWithoutQueryParamsToOriginalUrl[key];
      newSignedS3Urls[originalUrl] = value[0];
    });

    return newSignedS3Urls;
  }, [getSignedUrls]);

  useAsyncEffect(async () => {
    const expiredUrls: string[] = [];
    const newUrlsMapping: Record<string, string> = {};

    s3Urls.forEach((s3Url) => {
      const currentUrl = currentSignedS3Urls[s3Url] ?? s3Url;

      if (signedUrlIsExpired(currentUrl)) {
        expiredUrls.push(s3Url);
      } else if (!(s3Url in currentSignedS3Urls)) {
        newUrlsMapping[s3Url] = s3Url;
      }
    });

    const expiredUrlsMapping = await refreshExpiredUrls(expiredUrls);

    if (Object.keys(expiredUrlsMapping).length > 0 || Object.keys(newUrlsMapping).length > 0) {
      setCurrentSignedS3Urls((currentSignedS3Urls) => ({
        ...currentSignedS3Urls,
        ...newUrlsMapping,
        ...expiredUrlsMapping,
      }));
    }
  }, [s3Urls, refreshExpiredUrls]);

  return currentSignedS3Urls;
};
